import { useId, useState, type DetailedHTMLProps, type DialogHTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";

import { AriaLabelForContext } from "@/contexts/AriaLabelForContext";
import { multiref } from "@/multiref";

export function Dialog({
  children,
  closeOnOutside = false,
  ref,
  className,
  ...props
}: { closeOnOutside?: boolean } & DetailedHTMLProps<DialogHTMLAttributes<HTMLDialogElement>, HTMLDialogElement>) {
  const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);
  const [dialogContent, setDialogContent] = useState<HTMLDivElement | null>(null);
  const labelId = useId();

  function onClickOutside() {
    if (closeOnOutside) {
      dialog?.close();
    } else {
      dialogContent?.animate(
        [
          { transform: "rotate(0)" },
          { transform: "rotate(-1deg)" },
          { transform: "rotate(1deg)" },
          { transform: "rotate(-1deg)" },
          { transform: "rotate(1deg)" },
          { transform: "rotate(0)" },
        ],
        200,
      );
    }
  }

  return (
    <AriaLabelForContext value={labelId}>
      <dialog
        className="relative size-full max-h-screen max-w-screen bg-transparent backdrop:bg-black/25"
        ref={multiref(ref, setDialog)}
        aria-labelledby={labelId}
        {...props}
      >
        <div
          role="presentation"
          className="absolute inset-0 cursor-auto"
          onClick={onClickOutside}
          onPointerDown={(event) => event.stopPropagation()}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            className={twMerge(
              "pointer-events-auto absolute m-auto max-w-full origin-center overflow-auto rounded-sm bg-white shadow-lg",
              className,
            )}
            ref={setDialogContent}
          >
            {children}
          </div>
        </div>
      </dialog>
    </AriaLabelForContext>
  );
}
